import { useState, useMemo, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';

import { handleCheckboxForUpdateEntityDetails, getNonEditableFieldsForRow } from './utils';

import { ContentAndTitle, TabTitle } from '../.';
import { TableWithComment } from '../../..';
import { FEDERAL_UUID, LEVELS } from '../../../../constants';
import { useCurrencies, useFinancialData, useCompletionStatus } from '../../../../hooks';
import { ValuationAllowanceTabProps, Level, Step } from '../../../../models';
import LoadingWrapper from '../../../LoadingWrapper';
import {
  EntityNumberRouteMatch,
  getValuationAllowancesColumns,
  getTableDataFromFinancialData,
  handleEditRowForEntityDetails,
  handleOnCellOrCommentBlurForEntityDetails
} from '../../utils';

const useStyles = makeStyles(() => ({
  tableContainer: {
    position: 'relative',
    height: '100%',
    overflow: 'auto'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    pointerEvents: 'all'
  }
}));

const LEVEL: Level = LEVELS.VALUATION_ALLOWANCES;
const STEP: Step = 'adjustment';
const STEPS: Step[] = [STEP];
const LevelAndStep = `${LEVEL}.${STEP}`;

const ValuationAllowanceAdjustment = ({
  entityId,
  subJurisdictions,
  entityJurisdiction
}: ValuationAllowanceTabProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [rows, setRows] = useState<any>([]);
  const [isCheckboxLoading, setIsCheckboxLoading] = useState(false);
  const dispatch = useDispatch();
  const {
    params: { entityNumber }
  } = useRouteMatch<EntityNumberRouteMatch>();
  const { tabsData, failedCells, isFetchLoading } = useFinancialData(entityNumber, LEVEL, STEPS);
  const { currencyByEntityIdMap } = useCurrencies();
  const currencyIsoCode = currencyByEntityIdMap[entityId]?.isoCode;
  const financialInfo = useMemo(() => tabsData[LevelAndStep] ?? [], [tabsData]);
  const { stepCompletionStatus } = useCompletionStatus(entityNumber, LevelAndStep, FEDERAL_UUID);
  const jurisdictions = useMemo(() => {
    const sortedSubJurisdictions = [...subJurisdictions].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return entityJurisdiction ? [entityJurisdiction, ...sortedSubJurisdictions] : sortedSubJurisdictions;
  }, [entityJurisdiction, subJurisdictions]);
  const rowNames = useMemo(() => jurisdictions.map((jurisdiction) => ({ name: jurisdiction.name })), [jurisdictions]);

  const jurisdictionIdByName = useMemo(() => Object.fromEntries(jurisdictions.map(({ name, id }) => [name, id])), [
    jurisdictions
  ]);

  const rowsWithData = useMemo(() => getTableDataFromFinancialData(rowNames, financialInfo), [financialInfo, rowNames]);

  useEffect(() => {
    const rowsWithReadOnlyFields = rowsWithData.map((row) => ({
      ...row,
      nonEditableFields: getNonEditableFieldsForRow(row)
    }));
    setRows(rowsWithReadOnlyFields);
  }, [rowsWithData]);

  const columns = getValuationAllowancesColumns(t, !stepCompletionStatus.status);

  const dataForMethods = {
    columns,
    dispatch,
    entityId,
    entityNumber,
    financialInfo,
    level: LEVEL,
    rows,
    setRows,
    step: STEP,
    t
  };

  return (
    <LoadingWrapper isLoading={isFetchLoading}>
      <ContentAndTitle
        title={
          <TabTitle
            currencyIsoCode={currencyIsoCode}
            title={t('valuation-allowances-by-jurisdiction')}
            shouldDisplayCompletedToggle={false}
            isCompleted={false}
          />
        }
      >
        <Box className={classes.tableContainer}>
          <TableWithComment
            columns={columns}
            failedCells={failedCells}
            rows={rows}
            hideActionsMenu={stepCompletionStatus.status}
            onCellChange={(params) => {
              handleEditRowForEntityDetails({
                ...dataForMethods,
                ...params,
                jurisdictionId: jurisdictionIdByName[params.row.name ?? '']
              });
            }}
            onCellOrCommentBlur={(params) => {
              if (params.column.isCheckbox) {
                if (isCheckboxLoading) {
                  return;
                }

                setIsCheckboxLoading(true);
                handleCheckboxForUpdateEntityDetails({
                  ...dataForMethods,
                  ...params,
                  jurisdictionId: jurisdictionIdByName[params.row.name ?? '']
                }).finally(() => {
                  setIsCheckboxLoading(false);
                });
              } else {
                handleOnCellOrCommentBlurForEntityDetails({
                  ...dataForMethods,
                  ...params,
                  jurisdictionId: jurisdictionIdByName[params.row.name ?? '']
                });
              }
            }}
          />
          {isCheckboxLoading && (
            <Box className={classes.loadingOverlay}>
              <CircularProgress size={40} />
            </Box>
          )}
        </Box>
      </ContentAndTitle>
    </LoadingWrapper>
  );
};

export default ValuationAllowanceAdjustment;
